import React, { forwardRef, KeyboardEvent, useEffect } from 'react';
import { isArray } from 'lodash';
import { sortingOrder } from 'utils/sort';
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import { KakarikoStickyElementContextType } from '../../StickyElement/types';
import { KakarikoStickyElementContext } from '../../StickyElement';
import { GridContext } from '..';
import { KakarikoGridContextType } from '../types';
import {
  StyledKakarikoGridHeaderCell,
  StyledKakarikoGridHeader,
} from './styles';
import {
  IKakarikoGridHeaderProps,
  IKakarikoGridHeaderCellProps,
} from './types';

export const KakarikoGridHeaderCell = (props: IKakarikoGridHeaderCellProps) => {
  const {
    children,
    fractionWidth = 1,
    sortName,
    sortType,
    styles,
    tabIndex = 0,
    tableSortable = false,
    $textAlignment,
  } = props;

  const {
    isLoading,
    sortFieldState,
    sortOrderState,
    tableTheme,
    setFractionWidth,
    setSortProps,
  } = React.useContext<KakarikoGridContextType>(GridContext);

  const { isClone } = React.useContext<KakarikoStickyElementContextType>(
    KakarikoStickyElementContext,
  );

  const ascending =
    sortFieldState === sortName && sortOrderState === sortingOrder.ASC;
  const descending =
    sortFieldState === sortName && sortOrderState === sortingOrder.DESC;

  // On focus, Space and Enter should trigger sorting.
  const isTriggerKey = (key: KeyboardEvent<Element>): boolean => {
    key.preventDefault();
    return ['Enter', 'Space'].includes(key.code);
  };
  const sort = (trigger: boolean) =>
    trigger && setSortProps?.(sortName || '', sortType);
  const isSortable = tableSortable && sortName;

  // Adds the header to the column count if it gets render. If it is behind an AuthorizationComponent that returns null,
  // it will not be called.
  useEffect(() => {
    if (!isClone) {
      setFractionWidth?.((prev: any) => ({
        fractionWidth: [...prev.fractionWidth, fractionWidth],
      }));
    }
  }, [fractionWidth, isClone, setFractionWidth]);

  return (
    <StyledKakarikoGridHeaderCell
      style={styles}
      onClick={() => !isLoading && isSortable && sort(true)}
      onKeyUp={(e: React.KeyboardEvent) => {
        return !isLoading && isSortable && sort(isTriggerKey(e));
      }}
      tabIndex={tabIndex}
      $tableSortable={tableSortable}
      $textAlignment={$textAlignment}
      className={`${tableTheme}${isLoading ? ' disabled' : ''}`}
    >
      {children}
      {sortName && (
        <div className="data-grid__header-sort">
          {!descending && <ArrowDropUp data-testid="ascending" />}
          {!ascending && <ArrowDropDown data-testid="descending" />}
        </div>
      )}
    </StyledKakarikoGridHeaderCell>
  );
};

export const isHeaderCell = (
  element: React.ReactElement | React.ReactElement[],
): boolean => {
  if (isArray(element)) return false;
  if (typeof element.type === 'string') return false;
  const headerCells = [KakarikoGridHeaderCell.name];
  return headerCells.includes(element.type.name);
};

export const KakarikoGridHeader: React.FC<IKakarikoGridHeaderProps> =
  forwardRef((props: IKakarikoGridHeaderProps, ref: any) => {
    const { children, tableSortable, $textAlignment } = props;

    const { isClone } = React.useContext<KakarikoStickyElementContextType>(
      KakarikoStickyElementContext,
    );

    // Clone is used to nest the props into a `passedProps` key if needed to pass through
    // an AuthorizationComponent to the underlying KakarikoGridHeaderCell.
    const clone = (
      element: React.ReactElement,
      key: number | string,
      props?: { [key: string]: any },
    ) => {
      const child =
        typeof element.props?.children !== 'string' && element.props?.children;

      const shouldPassProps = (child: any) => !!(child && isHeaderCell(child));

      const gridElement = child || element;
      const passedProps = {
        key: `header-${key}`,
        tabIndex: 0,
        ...props,
        ...(isHeaderCell(gridElement) && {
          key: `header-child-${key}`,
          tableSortable,
          $textAlignment,
        }),
      };

      return React.cloneElement(element, {
        key,
        ...(shouldPassProps(child)
          ? { passedProps: passedProps }
          : { ...passedProps }),
        ...props,
      });
    };

    // Resets the number of columns to 0 when the header is destroyed so that the columns don't grow linearly.
    const { setFractionWidth } =
      React.useContext<KakarikoGridContextType>(GridContext);
    useEffect(() => {
      return () => {
        if (!isClone) setFractionWidth?.(() => ({ fractionWidth: [] }));
      };
    }, [isClone, setFractionWidth]);

    return (
      <StyledKakarikoGridHeader ref={ref}>
        {children
          .filter((child: React.ReactElement | boolean) =>
            React.isValidElement(child),
          )
          .map((child: React.ReactElement, index: number) =>
            clone(child, index),
          )}
      </StyledKakarikoGridHeader>
    );
  });

KakarikoGridHeader.displayName = 'KakarikoGridHeader';
